import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Menu, X, User, Bell } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/servizi", label: "Servizi" },
  { to: "/kima-match", label: "KIMA Match" },
  { to: "/nail-artists", label: "Nail Artists" },
  { to: "/galleria", label: "Galleria" },
  { to: "/looks", label: "KIMA Looks" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-offwhite/70 border-b border-espresso/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="font-serif text-3xl tracking-tight text-espresso">KIMA</Link>
        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label}`} className="text-sm text-espresso/80 hover:text-burgundy transition-colors">{l.label}</Link>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-4">
          {user ? (
            <>
              <NotificationBell />
              <Link to={user.role === "client" ? "/kima-id" : "/admin"} data-testid="nav-account" className="flex items-center gap-2 text-sm text-espresso hover:text-burgundy transition-colors"><User size={18} /> {user.name}</Link>
              <button onClick={async () => { await logout(); nav("/"); }} data-testid="nav-logout" className="text-sm text-taupe hover:text-espresso transition-colors">Esci</button>
            </>
          ) : (
            <Link to="/accedi" data-testid="nav-login" className="text-sm text-espresso/80 hover:text-burgundy transition-colors">Accedi</Link>
          )}
          <Link to="/prenota" data-testid="nav-book" className="bg-espresso text-ivory text-sm px-6 py-3 rounded-sm hover:bg-burgundy transition-colors">Prenota</Link>
        </div>
        <button className="lg:hidden text-espresso" onClick={() => setOpen(!open)} data-testid="nav-menu-toggle">{open ? <X /> : <Menu />}</button>
      </div>
      {open && (
        <div className="lg:hidden bg-offwhite border-t border-espresso/10 px-6 py-4 flex flex-col gap-4">
          {links.map((l) => (<Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-espresso">{l.label}</Link>))}
          {user ? (
            <>
              <Link to={user.role === "client" ? "/kima-id" : "/admin"} onClick={() => setOpen(false)} className="text-espresso">Il mio KIMA ID</Link>
              <button onClick={async () => { await logout(); setOpen(false); nav("/"); }} className="text-left text-taupe">Esci</button>
            </>
          ) : (<Link to="/accedi" onClick={() => setOpen(false)} className="text-espresso">Accedi</Link>)}
          <Link to="/prenota" onClick={() => setOpen(false)} className="bg-espresso text-ivory text-center py-3 rounded-sm">Prenota</Link>
        </div>
      )}
    </header>
  );
}

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = () => api.get("/my/notifications").then((r) => { setItems(r.data.notifications || []); setUnread(r.data.unread_count || 0); }).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try { await api.post("/my/notifications/read"); setUnread(0); setItems((p) => p.map((n) => ({ ...n, read: true }))); } catch {}
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="notif-bell" className="relative text-espresso hover:text-burgundy transition-colors p-1">
        <Bell size={20} />
        {unread > 0 && <span data-testid="notif-badge" className="absolute -top-1 -right-1 bg-burgundy text-ivory text-[10px] leading-none rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <div data-testid="notif-panel" className="absolute right-0 mt-3 w-80 max-h-96 overflow-y-auto bg-ivory border border-espresso/10 rounded-sm shadow-xl z-50">
          <div className="px-4 py-3 border-b border-espresso/10 kima-overline text-taupe">Notifiche</div>
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-espresso/50">Nessuna notifica.</div>
          ) : (
            items.map((n, i) => (
              <div key={i} data-testid="notif-item" className={`px-4 py-3 border-b border-espresso/5 ${n.read ? "" : "bg-offwhite"}`}>
                <div className="text-sm font-medium text-espresso">{n.title}</div>
                <div className="text-xs text-espresso/60 mt-0.5">{n.message}</div>
                <div className="text-[10px] text-taupe mt-1">{fmtDate((n.at || "").slice(0, 10))}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
